import { createTheme } from '@mui/material/styles';

// Read a `--var` token (R G B triple) from :root and return a usable rgb() string.
// SSR-safe: falls back to the DeepSync brand primary (#2563eb) when window/document
// is unavailable or the property isn't set yet.
const cssVar = (name: string, fallback = '37 99 235'): string => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return `rgb(${fallback})`;
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return `rgb(${value || fallback})`;
};

const createAppTheme = (mode: 'light' | 'dark') => createTheme({
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
  },
  palette: {
    mode,
    // Brand primary sourced from CSS variable --brand (#2563eb light / #60a5fa dark).
    primary: { main: cssVar('--brand') },
    secondary: { main: cssVar('--brand') },
    background: {
      default: mode === 'dark' ? '#0f172a' : '#f8fafc',
      paper: mode === 'dark' ? '#1e293b' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#f1f5f9' : '#0f172a',
      secondary: mode === 'dark' ? '#94a3b8' : '#64748b',
    },
    success: { main: '#22c55e' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
    info: { main: '#0ea5e9' },
    grey: mode === 'dark' 
      ? { 50: '#1e293b', 100: '#334155', 200: '#475569', 300: '#64748b', 400: '#94a3b8', 500: '#cbd5e1', 600: '#e2e8f0', 700: '#f1f5f9', 800: '#f8fafc', 900: '#ffffff' }
      : { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a' },
    divider: mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.12)',
  },
  shape: { borderRadius: 8 },
  components: {
    MuiPaper: { 
      styleOverrides: { 
        root: { 
          backgroundImage: 'none',
        } 
      }, 
      defaultProps: { elevation: 1 } 
    },
    MuiAppBar: { 
      styleOverrides: { 
        root: { 
          backgroundColor: 'transparent', 
          boxShadow: 'none' 
        } 
      } 
    },
    MuiButton: { 
      styleOverrides: { 
        root: { 
          borderRadius: 8, 
          textTransform: 'none', 
          fontWeight: 600,
        } 
      } 
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'dark' ? '#1e293b' : '#334155',
          color: '#f8fafc',
          border: mode === 'dark' ? '1px solid rgba(148,163,184,0.2)' : 'none',
          fontSize: '0.75rem',
          padding: '6px 12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        arrow: {
          color: mode === 'dark' ? '#1e293b' : '#334155',
        }
      }
    },
  },
} as const);

export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
export const theme = darkTheme; // Default to dark for the always-dark portals if imported directly
